// GA4 event names (snake_case). Keep in sync with ANALYTICS.md §8 "Event taxonomy".
// Use with track(EVENTS.OFFER_SUBMITTED, { service_type, request_id }).
//
// `login`, `logout` and `search` keep GA4's own recommended names verbatim so we
// inherit its built-in reports; everything else follows `object_action`, verb last,
// past tense.
//
// This file is the SHARED taxonomy: it holds every portal's events and is byte-identical
// in supplier-fe, admin-fe and agent-fe (see scripts/check-analytics-drift.sh). An app
// only calls its own section, but the map itself does not fork — a diverged EVENTS map is
// how the same action ends up recorded under two names and every funnel silently splits.
export const EVENTS = {
  LOGIN: "login",
  LOGOUT: "logout",
  SEARCH: "search",
  FILTER_APPLIED: "filter_applied",
  ERROR_SHOWN: "error_shown",

  // Supplier
  OFFER_SUBMITTED: "offer_submitted",
  OFFER_PRICE_SET: "offer_price_set",
  REQUEST_VIEWED: "request_viewed",
  SERVICE_CONFIRMED: "service_confirmed",
  LOGO_UPLOADED: "logo_uploaded",
  // Referral landing pages (supplier, 2026-10-19)
  REFERRAL_LINK_CREATED: "referral_link_created",
  REFERRAL_LINK_STYLE_CHANGED: "referral_link_style_changed",
  REFERRAL_LINK_COPIED: "referral_link_copied",
  REFERRAL_LANDING_PREVIEWED: "referral_landing_previewed",
  REFERRAL_LANDING_VIEWED: "referral_landing_viewed",
  REFERRAL_LANDING_CTA_CLICKED: "referral_landing_cta_clicked",
  SIGN_UP_STARTED: "sign_up_started",
  // GA4's recommended name, kept verbatim for its built-in sign-up report.
  SIGN_UP: "sign_up",

  // Admin
  SUPPLIER_APPROVED: "supplier_approved",
  SUPPLIER_REJECTED: "supplier_rejected",
  INVOICE_VIEWED: "invoice_viewed",
  ROLE_ASSIGNED: "role_assigned",
  REQUEST_ASSIGNED: "request_assigned",

  // Agent
  REQUEST_OPENED: "request_opened",
  OFFER_FORWARDED: "offer_forwarded",
  OFFER_PACKAGE_REVIEWED: "offer_package_reviewed",
  CUSTOMER_MESSAGED: "customer_messaged",
  QUOTE_SENT: "quote_sent",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * The service catalogue as the backend actually models it, not the five tiles the
 * mobile home screen happens to show. ANALYTICS.md §8 originally listed only
 * `jet | car | yacht | hotel | shop`, which left the portals nowhere to put a
 * restaurant reservation, an event or personal assistance — and "nowhere to put it"
 * in practice means someone files it under `shop` and the report quietly lies.
 */
export type ServiceType =
  | "jet"
  | "car"
  | "hotel"
  | "restaurant"
  | "yacht"
  | "event"
  | "assistance"
  | "shop"
  | "other";

/**
 * Backend `serviceId` → taxonomy `service_type`. The portals only ever hold the
 * numeric id, so the mapping has to live somewhere; it lives here, next to the
 * union, so admin/agent/supplier cannot disagree about what id 9 is.
 *
 * Several ids collapse onto one type on purpose (2/9/10 are all ground transport,
 * 3/5 are both accommodation) — that is how the product presents them.
 */
export const SERVICE_TYPE_BY_ID: Record<number, ServiceType> = {
  1: "jet",
  2: "car",
  3: "hotel",
  4: "restaurant",
  5: "hotel",
  6: "yacht",
  7: "event",
  8: "assistance",
  9: "car",
  10: "car",
};

/**
 * A numeric id with no entry maps to `"other"` rather than to nothing: that is how a
 * newly launched service announces itself in the reports instead of silently
 * vanishing from every service-segmented breakdown. A missing or non-numeric id
 * returns `undefined` — there is genuinely nothing to classify, and an absent
 * dimension beats a wrong one.
 */
export const serviceTypeFromId = (
  serviceId?: number | string | null
): ServiceType | undefined => {
  if (serviceId === null || serviceId === undefined || serviceId === "") return undefined;
  const id = Number(serviceId);
  if (!Number.isFinite(id)) return undefined;
  return SERVICE_TYPE_BY_ID[id] ?? "other";
};

/** A referral link's landing design, lower-cased from the backend enum. */
export type LandingStyleParam = "none" | "luxury_experiences" | "luxury_hotels" | "private_charter";

/**
 * Payload shape per event, keyed by the event name — this is what makes `track`
 * type-safe (B7). Adding an event to EVENTS without adding it here is a compile
 * error, which is the point: an unnamed payload is how untyped `Record<string, any>`
 * grew PII in the first place.
 *
 * Two standing rules, enforced by the types where possible:
 *   - **No free text.** Nothing a user typed goes in a param. `search` carries a
 *     result count, never the query (see §9's never-send list). `filter_value` takes
 *     the enum value from a select, never an input's contents.
 *   - **Ids only.** `request_id`/`offer_id` are opaque backend ids, not references
 *     that embed a customer name.
 */
export type EventPayloads = {
  login: { method?: "password" | "refresh_token" };
  logout: { reason?: "user" | "session_expired" };
  /** Result count only — the query string itself is never sent. */
  search: { scope?: string; result_count?: number };
  filter_applied: { filter_name: string; filter_value?: string | number | boolean };
  error_shown: { error_scope: string; error_code?: string | number };

  offer_submitted: { service_type: ServiceType; request_id: string };
  offer_price_set: { service_type: ServiceType; request_id: string };
  request_viewed: { request_id: string; service_type?: ServiceType };
  service_confirmed: { service_type: ServiceType; request_id?: string };
  logo_uploaded: Record<string, never>;
  /** Never the referral code: whoever holds it is credited for signups. The link's numeric id only. */
  referral_link_created: { landing_style: LandingStyleParam };
  referral_link_style_changed: { landing_style: LandingStyleParam; previous_landing_style: LandingStyleParam };
  referral_link_copied: { landing_style: LandingStyleParam };
  referral_landing_previewed: { landing_style: LandingStyleParam };
  referral_landing_viewed: { landing_style: LandingStyleParam; referral_link_id?: string; link_status: "active" | "preview" };
  referral_landing_cta_clicked: { landing_style: LandingStyleParam; referral_link_id?: string; cta_position: "hero" | "closing" };
  sign_up_started: { referred: boolean; landing_style: LandingStyleParam; supplier_type?: "business" | "individual" };
  sign_up: { method: "email"; referred: boolean; landing_style: LandingStyleParam; supplier_type: "business" | "individual"; affiliate_only: boolean };

  /**
   * The id is the *application's*, which is what the approve/reject endpoint takes —
   * a supplier record does not exist yet at that point. Deliberately not called
   * `supplier_id`: that name is a registered user-property dimension (supplier-fe),
   * and the same key meaning two different things is how a report starts lying.
   */
  supplier_approved: { application_id: string };
  /** `reason_code` is an enumerated decision code — never the free-text rejection note. */
  supplier_rejected: { application_id: string; reason_code?: string };
  invoice_viewed: { invoice_id: string };
  /** The role's name is configuration, not personal data; the target user is an id. */
  role_assigned: { role_name: string; target_user_id?: string };
  request_assigned: { request_id: string; service_type?: ServiceType };

  request_opened: { request_id: string; service_type?: ServiceType };
  offer_forwarded: { offer_id: string; request_id?: string };
  /**
   * The supervisor's decision on an assembled package. Deliberately NOT `offer_accepted`:
   * that name is a **key event** meaning the *customer* accepted, and firing it on an
   * internal approval would inflate a conversion count with our own staff's clicks.
   */
  offer_package_reviewed: { offer_package_id: string; decision: "approved" | "rejected" };
  /** The kind of message, never its contents and never a length (§9's never-send list). */
  customer_messaged: { message_type: "text" | "audio" | "other" };
  quote_sent: { request_id: string; service_type?: ServiceType };
};
